import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild, computed, effect, signal } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { Subject } from 'rxjs';
import { RestApiConditionType } from 'src/app/models/RestApiCaller/Enums/rest-api-condition-type';
import { RestApiCondition } from 'src/app/models/RestApiCaller/conditions/rest-api-condition';
import { RestApiResponseVariableConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-response-variable-configuration';
import { ConditionFormDialogResult } from './Models/condition-form-dialog-result';
import { ContextualMenuActions } from './Models/contextual-menu-actions';
import { ConditionFormDialogComponent } from './condition-form-dialog/condition-form-dialog.component';

/**
 * Composant de saisie des conditions d'un évènement.
 * (Utilisable dans un formulaire et dans un MatFormField)
 */
@Component({
    selector: 'app-conditions-form',
    imports: [MatTableModule, MatIconModule, MatMenuModule, MatButtonModule],
    templateUrl: './conditions-form.component.html',
    styleUrl: './conditions-form.component.scss',
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: ConditionsFormComponent,
        },
    ]
})
export class ConditionsFormComponent implements OnInit, OnDestroy, MatFormFieldControl<RestApiCondition[]> {
    /**
     * Liste des variables de la réponse.
     */
    @Input()
    public responseVariables: RestApiResponseVariableConfiguration[] = [];

    /**
     * Liste des actions du menu contextuel du tableau des conditions.
     */
    public ContextualMenuActions = ContextualMenuActions;

    /**
     * Liste des types de conditions et leur libellé.
     */
    public conditionTypes: Map<RestApiConditionType, string> = new Map<RestApiConditionType, string>();

    /**
     * Liste des colonnes affichées dans le tableau des conditions.
     */
    displayedColumns: string[] = ['name', 'type', 'isMainCondition'];

    /**
     * Liste des en-têtes affichées dans le tableau.
     */
    conditions = signal<RestApiCondition[]>([]);

    /**
     * Emet lorsque des changements surviennent dans le composant pour prevenir le MatFormField parent de déclencher le change detection.
     */
    stateChanges = new Subject<void>();

    /**
     * Associe les id des elements dont le contenu sera utilisé pour décrire ce composant à l'attribut aria.
     */
    @HostBinding('attr.aria-describedby') describedBy = '';

    /**
     * Définit les id des elements dont le contenu sera utilisé pour décrire ce composant.
     * @param ids Id des elements dont le contenu sera utilisé pour décrire ce composant.
     */
    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join('');
    }

    /**
     * Définit la valeur du contrôle.
     */
    @Input()
    set value(value: RestApiCondition[]) {
        this.conditions.set(value);
        this.stateChanges.next();
    }

    /**
     * Obtient la valeur du contrôle.
     */
    get value(): RestApiCondition[] {
        return this.conditions();
    }

    /**
     * Valeur de l'identifiant qui sera attribuée à la prochaine instance du contrôle.
     */
    static nextId = 0;

    /**
     * Définit la valeur du placeholder.
     */
    set placeholder(placeholder: string) {
        // Le placeholder n'est pas utilisé dans ce composant
    }

    /**
     * Obtient la valeur du placeholder.
     */
    get placeholder() {
        return null;
    }

    /**
     * Retourne une valeur indiquant si le contrôle est vide (absence d'en-tête)
     */
    get empty(): boolean {
        return this.conditions().length !== 0;
    }

    /**
     * Retourne une valeur indiquant si le libellé doit être flottant.
     * (non géré)
     */
    get shouldLabelFloat(): boolean {
        return false;
    }

    /**
     * Obtient une valeur indiquant si ce contrôle est obligatoire.
     */
    @Input()
    get required() {
        return this._required;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est obligatoire.
     */
    set required(value: boolean) {
        this._required = value;
        this.stateChanges.next();
    }

    /**
     * Valeur indiquant si le contrôle est obligatoire.
     */
    private _required = false;

    /**
     * Obtient une valeur indiquant si ce contrôle est désactivé.
     */
    @Input()
    get disabled() {
        return this._disabled;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est désactivé.
     */
    set disabled(value: boolean) {
        this._disabled = value;

        let actionColumnIndex = this.displayedColumns.findIndex((c) => c === 'actions');

        if (value === false) {
            if (actionColumnIndex === -1) {
                this.displayedColumns.push('actions');
            }
        } else {
            if (actionColumnIndex !== -1) {
                this.displayedColumns.splice(actionColumnIndex, 1);
            }
        }
    }

    /**
     * Valeur indiquant si ce contrôle est désactivé.
     */
    private _disabled = false;

    /**
     * Valeur indiquant si le contrôle possède le focus.
     */
    _focused: boolean = false;

    /**
     * Obtient une valeur indiquant si le contrôle possède le focus.
     */
    get focused() {
        return this._focused;
    }

    /**
     * Définit une valeur indiquant si le contrôle possède le focus.
     */
    set focused(value: boolean) {
        this._focused = value;
        this.onTouched(value);
    }

    /**
     * Id du contrôle.
     */
    @HostBinding()
    id = `ConditionsFormComponent-${ConditionsFormComponent.nextId++}`;

    /**
     * Réference à l'élément MatTable du template.
     */
    @ViewChild(MatTable, { read: ElementRef, static: true })
    table: ElementRef;

    /**
     * Constructeur.
     * @param focusMonitor : Service utilisé pour écouter les changements de focus.
     * @param dialog : Service utilisé pour ouvrir des boites de dialogue.
     * @param ngControl : Reference au contrôle dans le formulaire.
     */
    constructor(
        private focusMonitor: FocusMonitor,
        public dialog: MatDialog,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        computed(() => console.log('conditions', this.conditions));
        effect(() => {
            this.onChange(this.conditions());
        });

        this.conditionTypes.set(RestApiConditionType.HttpCode, 'Sur code HTTP');
        this.conditionTypes.set(RestApiConditionType.Variable, "Sur la valeur d'une variable");
        this.conditionTypes.set(RestApiConditionType.Logical, 'Sur une condition logique');
    }

    /**
     * Renseigne la valeur du contrôle.
     * (Appellé par le formulaire)
     * @param value : valeur du contrôle.
     */
    writeValue(value: RestApiCondition[]): void {
        this.value = value;
    }

    /**
     * Fonction à appeler lorsque la valeur du contrôle change.
     */
    private onChange;

    /**
     * Inscrit une fonction à appeler lorsque la valeur du contrôle change.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque la valeur du contrôle change.
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Fonction à appeler lorsque le contrôle gagne le focus.
     */
    private onTouched;

    /**
     * Inscrit une fonction à appeler lorsque le contrôle gagne le focus.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque le contrôle gagne le focus.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Specifie l'état 'désactivé' du contrôle.
     * (Appellé par le formulaire)
     * @param value : Valeur de l'état 'désactivé'.
     */
    setDisabledState(value: boolean): void {
        this.disabled = value;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le contrôle.
     * (Appellé par le formulaire)
     * @param event evnement clic de la souris.
     */
    onContainerClick(event: MouseEvent): void {
        // On ne fait rien de particulier lorsque l'on clique sur le tableau.
    }

    /**
     * Retourne une valeur indiquant si le contrôle est en erreur.
     */
    get errorState(): boolean {
        // On considère que ce control n'est jamais en erreur.
        return false;
    }

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.focusMonitor.monitor(this.table).subscribe((focused) => {
            this.focused = !!focused;
            this.stateChanges.next();
        });
    }

    /**
     * Appellé lors de la destruction du contrôle.
     */
    ngOnDestroy(): void {
        this.focusMonitor.stopMonitoring(this.table);
        this.stateChanges.complete();
    }

    /**
     * Appelé lorsque l'utilisateur double-clique sur un élément de la liste.
     */
    onMouseDoubleClick(row: RestApiCondition, event) {
        console.log('double clic', row);
        if (this.disabled) {
            this.displayCondition(row);
        } else {
            this.onEditCondition(row);
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Editer' du menu contextuel.
     * @param editedCondition : Instance de la condition a éditer.
     */
    public onEditCondition(editedCondition: RestApiCondition): void {
        console.log('edit', editedCondition);

        const dialogRef = this.dialog.open(ConditionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Édition de la condition',
                item: editedCondition,
                variables: this.responseVariables,
                referencedConditions: this.conditions(),
                readonly: this.disabled,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: ConditionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Mise à jour de la condition.
                console.log('update condition', dialogResult.item);
                this.conditions.update((conditions) => {
                    let updatedConditions = RestApiCondition.cloneArray(conditions);
                    const conditionIndex = conditions.findIndex((x) => x.id === editedCondition.id);
                    updatedConditions.splice(conditionIndex, 1, dialogResult.item);

                    return updatedConditions;
                });
            }
        });
    }

    /**
     * Affiche une condition en lecture seule.
     * @param condition : Instance de la condition a afficher.
     */
    public displayCondition(condition: RestApiCondition): void {
        console.log('display', condition);

        const dialogRef = this.dialog.open(ConditionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Consultation de la condition',
                item: condition,
                readonly: this.disabled,
                variables: this.responseVariables,
                referencedConditions: this.conditions(),
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        //dialogRef.afterClosed().subscribe((dialogResult: ConditionFormDialogResult) => {

        // });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton de création d'une condition.
     */
    public onCreateCondition(): void {
        console.log('create');

        const dialogRef = this.dialog.open(ConditionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une condition",
                item: new RestApiCondition(),
                variables: this.responseVariables,
                referencedConditions: this.conditions(),
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: ConditionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Création de la condition.
                console.log('create condition', dialogResult.item);
                dialogResult.item.id = this.getNextId();

                // S'il s'agit de la première condition alors elle est désigné comme principale.
                if (this.conditions().length === 0) {
                    dialogResult.item.isMainCondition = true;
                }

                this.conditions.update((conditions) => {
                    let updatedConditions = RestApiCondition.cloneArray(conditions);
                    updatedConditions.push(dialogResult.item);

                    return updatedConditions;
                });
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Dupliquer' du menu contextuel.
     * @param duplicatedCondition : Instance de la condition a dupliquer.
     */
    public onDuplicateCondition(duplicatedCondition: RestApiCondition): void {
        console.log('duplicate');
        duplicatedCondition = RestApiCondition.clone(duplicatedCondition);
        duplicatedCondition.id = null;
        duplicatedCondition.isMainCondition = false;

        const dialogRef = this.dialog.open(ConditionFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une condition",
                item: RestApiCondition.clone(duplicatedCondition),
                variables: this.responseVariables,
                referencedConditions: this.conditions(),
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: ConditionFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Création de la condition.
                console.log('create condition', dialogResult.item);
                dialogResult.item.id = this.getNextId();

                // S'il s'agit de la première condition alors elle est désigné comme principale.
                if (this.conditions().length === 0) {
                    dialogResult.item.isMainCondition = true;
                }

                this.conditions.update((conditions) => {
                    let updatedConditions = RestApiCondition.cloneArray(conditions);
                    updatedConditions.push(dialogResult.item);

                    return updatedConditions;
                });
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Supprimer' du menu contextuel.
     * @param event : Instance de la condition a supprimer.
     */
    public onDeleteCondition(event: RestApiCondition): void {
        console.log('delete', event);
        this.conditions.update((conditions) => conditions.filter((condition) => condition.id !== event.id));
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Désigner comme condition principale' du menu contextuel.
     * @param event : Instance de la condition a marquer.
     */
    public onMarkConditionAsMain(event: RestApiCondition): void {
        console.log('MarkAsMain', event);
        this.conditions.update((conditions) => {
            let updatedConditions = RestApiCondition.cloneArray(conditions);
            const previousMarkIndex = conditions.findIndex((condition) => condition.isMainCondition === true);

            if (previousMarkIndex !== -1) {
                updatedConditions[previousMarkIndex].isMainCondition = false;
            }

            const newMarkIndex = conditions.findIndex((condition) => condition.id === event.id);
            updatedConditions[newMarkIndex].isMainCondition = true;

            return updatedConditions;
        });
    }

    /**
     * Retourne l'id autogénéré pour la condition.
     * @returns un Id.
     */
    private getNextId() {
        let nextId = 1;

        if (this.conditions().length !== 0) {
            nextId = this.conditions().reduce((c1, c2) => (c1.id > c2.id ? c1 : c2)).id + 1;
        }

        return nextId;
    }
}
